import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Modal } from 'junto-ui';
import { useStore } from '@store';
import { PrivacyLabel } from 'components-core';
import AccountServices from '@ncl/modules/account/services/AccountServices';
import ResponsabilityTermService from '@ncl/modules/account/services/ResponsabilityTermService';
import * as services from './RequiredTermsModal.services';
import {
  Wrapper,
  Title,
  Text,
  ConfirmButton,
  Checkbox,
  LinkButton,
} from './RequiredTermsModalStyles';
import Resolution382Video from './fragments/Resolution382Video';

const RequiredTermsModal = () => {
  const sharedStore = useStore('sharedStore');
  const [termsStatus, setTermsStatus] = useState(services.getInitialStatus());
  const [displayResolution382Video, setDisplayResolution382Video] = useState(false);

  function onCheckboxChange(e) {
    const { target } = e;
    const { name } = target;

    const currentValue = termsStatus[name];
    setTermsStatus({ ...termsStatus, [name]: !currentValue });
  }

  function onBtnClick() {
    const { currentLiabilityTerms, currentPrivacyTerms, currentResolution382Terms } = sharedStore;
    if (currentLiabilityTerms) {
      const { id } = currentLiabilityTerms;
      AccountServices.acceptRequiredTerms(id);
    }
    if (currentPrivacyTerms) {
      const { id } = currentPrivacyTerms;
      AccountServices.acceptRequiredTerms(id);
    }
    if (currentResolution382Terms) {
      const { id } = currentResolution382Terms;
      AccountServices.acceptRequiredTerms(id);
    }
    sharedStore.closePendingTermsModal();
  }

  function displayLiabilityTerms() {
    const { currentLiabilityTerms } = sharedStore;
    if (!currentLiabilityTerms) return null;

    return (
      <>
        <Checkbox
          name={services.LIABILITY_KEY}
          id="liability-term"
          data-testid="liability-term"
          label={
            <PrivacyLabel>
              Li e aceito o{' '}
              <a
                href={ResponsabilityTermService.getTermDocument(currentLiabilityTerms.description,
                   currentLiabilityTerms.version)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Termo de Responsabilidade
              </a>
            </PrivacyLabel>
          }
          checked={termsStatus[services.LIABILITY_KEY]}
          onChange={onCheckboxChange}
        />
      </>
    );
  }

  function displayPrivacyTerms() {
    if (!sharedStore.currentPrivacyTerms) return null;
    const { description, version } = sharedStore.currentPrivacyTerms;
    return (
      <>
        <Checkbox
          name={services.PRIVACY_KEY}
          id="privacy-term"
          data-testid="privacy-term"
          label={
            <PrivacyLabel>
              Li e aceito a{' '}
              <a
                href={ResponsabilityTermService.getTermDocument(description,
                   version)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </a>
            </PrivacyLabel>
          }
          checked={termsStatus[services.PRIVACY_KEY]}
          onChange={onCheckboxChange}
        />
      </>
    );
  }

  function displayResolution382Terms() {
    if (!sharedStore.currentResolution382Terms) return null;
    return (
      <>
        <Checkbox
          name={services.RESOLUTION_382_KEY}
          id="resolution-382-term"
          data-testid="resolution-382-term"
          label={
            <PrivacyLabel>
              Assisti ao{' '}
              <LinkButton
                variant="checkbox"
                data-testid="resolution-382-check-button"
                onClick={() => setDisplayResolution382Video(true)}
              >
                treinamento
              </LinkButton>{' '}
              referente a norma de conduta
            </PrivacyLabel>
          }
          checked={termsStatus[services.RESOLUTION_382_KEY]}
          onChange={onCheckboxChange}
        />
      </>
    );
  }

  function getTextAccordingToRole() {
    const role = AccountServices.getRole();

    return role === 'Tomador'
      ? 'Atualizamos nossas políticas e termos. É importante que dedique um tempo para ler. Para continuar usando nossos sistemas, você deve aceitar os novos termos e políticas.'
      : 'Atualizamos informações referentes a conteúdos legislativos. É importante que você dedique um tempo para ver. Para continuar usando nossos sistemas, você deve aceitar os novos termos.';
  }

  function isButtonDisabled() {
    const { currentLiabilityTerms, currentPrivacyTerms, currentResolution382Terms } = sharedStore;
    if (currentLiabilityTerms && !termsStatus[services.LIABILITY_KEY]) {
      return true;
    }
    if (currentPrivacyTerms && !termsStatus[services.PRIVACY_KEY]) {
      return true;
    }
    if (currentResolution382Terms && !termsStatus[services.RESOLUTION_382_KEY]) {
      return true;
    }
    return false;
  }
  const [embedId, setEmbedId] = useState('');

  useEffect(() => {
    const handleEmbedId = async () => {
      if (!sharedStore.currentResolution382Terms) return;
     const { description, version } = sharedStore.currentResolution382Terms;
    const { data } = await ResponsabilityTermService.getYoutubeEmbedId(description, version);
    setEmbedId(data);
  };
  handleEmbedId();
  }, [sharedStore.currentResolution382Terms]);

  function renderContent() {
    const { currentLiabilityTerms, currentPrivacyTerms, currentResolution382Terms } = sharedStore;
    if (displayResolution382Video) {
      return (<Resolution382Video
        embedId={embedId}
        onBackClick={() => setDisplayResolution382Video(false)}
      />);
    }
    if (
      currentResolution382Terms &&
      currentResolution382Terms.recurrence &&
      !currentLiabilityTerms &&
      !currentPrivacyTerms
    ) {
      return (
        <Wrapper>
          <Title data-testid="term-modal-title">Antes de seguir!</Title>
          <Text>
            Em cumprimento da Resolução CNSP nº 382/2020, precisamos que você reveja o{' '}
            <LinkButton
              variant="text"
              data-testid="resolution-382-check-button"
              onClick={() => setDisplayResolution382Video(true)}
            >
              Treinamento da Norma de Conduta.
            </LinkButton>{' '}
            Para continuar, confirme no botão a seguir que você acessou o conteúdo.
          </Text>
          <ConfirmButton
            noMargin
            variant="primary"
            onClick={() => onBtnClick(true)}
            data-testid="term-confirm-btn"
          >
            Confirmar e continuar
          </ConfirmButton>
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <Title data-testid="term-modal-title">Antes de seguir!</Title>
        <Text>{getTextAccordingToRole()}</Text>
        {displayLiabilityTerms()}
        {displayPrivacyTerms()}
        {displayResolution382Terms()}
        <ConfirmButton
          variant="primary"
          onClick={() => onBtnClick(false)}
          data-testid="term-confirm-btn"
          disabled={isButtonDisabled()}
        >
          Aceitar e continuar
        </ConfirmButton>
      </Wrapper>
    );
  }

  return (
    <Modal
      isOpen={sharedStore.displayPendingTermsModal}
      mobileFullScreen
      hideCloseButton
      width={displayResolution382Video ? '640px' : '450px'}
    >
      {renderContent()}
    </Modal>
  );
};

export default observer(RequiredTermsModal);
